/**
 * Tauri adapter — uses `@tauri-apps/plugin-sql` (SQLite under the hood on every
 * Tauri 2 target: Windows, macOS, Linux, Android, iOS).
 *
 * Loaded lazily so the package itself doesn't pull in Tauri types on the web build.
 */
import type { LocalDb, LocalDbFactory, LocalDbRow, SqlValue } from "../local-db.js";

export const tauriLocalDb: LocalDbFactory = {
  async open(name) {
    const { default: Database } = await import("@tauri-apps/plugin-sql");
    const db = await Database.load(`sqlite:${name}`);

    const wrap = (raw: typeof db): LocalDb => ({
      async exec(sql, params = []) {
        const r = await raw.execute(sql, params as unknown[]);
        return {
          rowsAffected: r.rowsAffected ?? 0,
          lastInsertId: r.lastInsertId,
        };
      },
      async query<T extends LocalDbRow = LocalDbRow>(sql: string, params: SqlValue[] = []) {
        return (await raw.select<T[]>(sql, params as unknown[])) as T[];
      },
      async transaction(fn) {
        await raw.execute("BEGIN", []);
        try {
          const result = await fn(wrap(raw));
          await raw.execute("COMMIT", []);
          return result;
        } catch (err) {
          await raw.execute("ROLLBACK", []);
          throw err;
        }
      },
      async close() {
        await raw.close();
      },
    });

    return wrap(db);
  },
};
